import { sql } from 'drizzle-orm';
import {
  pgTable,
  index,
  jsonb,
  timestamp,
  varchar,
  text,
  integer,
  decimal,
  boolean,
  uuid,
  primaryKey,
  date
} from "drizzle-orm/pg-core";
import { roleTypeEnum } from './enums';
import { organizations } from './organizations';

// User storage table (required for Replit Auth)
export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: varchar("email").unique(),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  profileImageUrl: varchar("profile_image_url"),
  role: varchar("role").notNull().default("adjuster"), // admin, adjuster, viewer
  adjusterLevel: varchar("adjuster_level").default("regular"), // regular, senior, director
  adjusterTypes: jsonb("adjuster_types"), // Array of adjuster specializations: sales, field_adjusting, desk_adjusting, appraisal, administrative, management, etc.
  isAppraiser: boolean("is_appraiser").default(false),
  isSystem: boolean("is_system").default(false),
  isDirectorOfAppraisals: boolean("is_director_of_appraisals").default(false),
  isSuperAdmin: boolean("is_super_admin").default(false),
  defaultCommissionRates: jsonb("default_commission_rates"), // Store custom commission rates per adjuster
  
  // Profile data from onboarding
  profileCompleted: boolean("profile_completed").default(false), // Track if onboarding is complete
  
  // Professional Background
  yearsExperience: integer("years_experience"), // Years in public adjusting
  previousExperience: jsonb("previous_experience"), // Array of previous industries/roles
  educationLevel: varchar("education_level"), // high_school, associates, bachelors, masters, doctorate, other
  educationField: varchar("education_field"), // Field of study if applicable
  
  // Work Specializations
  preferredClaimTypes: jsonb("preferred_claim_types"), // Array of peril types they prefer
  preferredPropertyTypes: jsonb("preferred_property_types"), // residential, commercial, industrial
  serviceAreas: jsonb("service_areas"), // Geographic areas they cover (states/cities)
  specializations: jsonb("specializations"), // Special areas of expertise
  
  // Licenses & Certifications
  publicAdjusterLicenses: jsonb("public_adjuster_licenses"), // Array of state licenses
  appraiserCertifications: jsonb("appraiser_certifications"), // Appraiser certifications
  otherCertifications: jsonb("other_certifications"), // Other professional certifications
  
  // Work Preferences
  preferredCommunication: jsonb("preferred_communication"), // email, phone, text, video
  availabilityHours: jsonb("availability_hours"), // Work schedule preferences
  remoteWorkCapable: boolean("remote_work_capable").default(true),
  travelWillingness: varchar("travel_willingness"), // local, regional, state, national, none
  maxTravelDistance: integer("max_travel_distance"), // Miles willing to travel
  
  // Professional Goals & Preferences
  careerGoals: jsonb("career_goals"), // Array of career objectives
  skillDevelopmentAreas: jsonb("skill_development_areas"), // Areas they want to improve
  commissionPreferences: jsonb("commission_preferences"), // Preferred commission structure
  workloadPreferences: varchar("workload_preference"), // light, moderate, heavy
  
  // Additional Profile Information
  phoneNumber: varchar("phone_number"),
  emergencyContact: jsonb("emergency_contact"), // Name, relationship, phone
  notes: text("notes"), // Admin notes about the user
  
  // Home/Office Location (for map display)
  homeAddress: text("home_address"),
  homeCity: varchar("home_city"),
  homeState: varchar("home_state"),
  homeZip: varchar("home_zip"),
  emailSignature: text("email_signature"), // User's email signature for outgoing emails
  cvFileUrl: varchar("cv_file_url"), // URL to CV/resume file in object storage
  educationHistory: jsonb("education_history"), // Array of {degree, institution, fieldOfStudy, graduationYear, description}
  professionalDesignations: jsonb("professional_designations"), // Array of {name, issuingOrganization, credentialId, dateObtained, expirationDate, description}
  
  // Professional Performance & Metrics
  avgSettlementValue: decimal("avg_settlement_value", { precision: 12, scale: 2 }), // Average claim settlement amount
  settlementSuccessRate: decimal("settlement_success_rate", { precision: 5, scale: 2 }), // Percentage of claims with improved payouts
  avgClaimClosureTime: integer("avg_claim_closure_time"), // Average days to close a claim
  
  // Regulatory & Compliance
  licenseExpirations: jsonb("license_expirations"), // Array of {state, expirationDate, alertDays}
  bondExpirationDate: date("bond_expiration_date"), // PA surety bond expiration date
  continuingEducationCredits: jsonb("continuing_education_credits"), // {required, completed, year, courses: []}
  multiStateLicensing: jsonb("multi_state_licensing"), // Array of {state, licenseNumber, effectiveDate, expirationDate, status}
  
  // Communication & Availability
  responseTimeSLA: integer("response_time_sla"), // Expected response time in hours
  outOfOfficeCalendar: jsonb("out_of_office_calendar"), // Array of {startDate, endDate, reason, autoRespond}
  
  // Financial & Business Development
  lifetimeRevenue: decimal("lifetime_revenue", { precision: 12, scale: 2 }), // Total commission/fees generated
  hourlyRate: decimal("hourly_rate", { precision: 10, scale: 2 }),
  
  // QuickBooks Online Integration - Adjuster as Vendor for commission payments
  qboVendorId: varchar("qbo_vendor_id"), // QBO Vendor ID when synced
  qboSyncedAt: timestamp("qbo_synced_at"), // Last successful sync timestamp
  
  activeOrgId: uuid("active_org_id").references(() => organizations.id, { onDelete: "set null" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});


// User Settings table for personal preferences
export const userSettings = pgTable("user_settings", {
  userId: varchar("user_id").primaryKey().references(() => users.id, { onDelete: "cascade" }),
  
  // UI Preferences
  theme: varchar("theme").default("light"), // light, dark, system
  language: varchar("language").default("en"),
  
  // Notification Preferences
  emailNotifications: boolean("email_notifications").default(true),
  smsNotifications: boolean("sms_notifications").default(true),
  
  // Future extensibility
  metadata: jsonb("metadata"), // For additional settings without schema changes
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});


// User Login Logs table
export const userLoginLogs = pgTable("user_login_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  email: varchar("email").notNull(),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  loginTime: timestamp("login_time").defaultNow().notNull(),
  ipAddress: varchar("ip_address"),
  userAgent: text("user_agent"),
  success: boolean("success").notNull().default(true),
  failureReason: text("failure_reason"),
}, (table) => [
  index("user_login_logs_user_id_idx").on(table.userId),
  index("user_login_logs_login_time_idx").on(table.loginTime),
  index("user_login_logs_email_idx").on(table.email),
]);


// Roles table for RBAC
export const roles = pgTable("roles", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name").notNull().unique(),
  description: text("description"),
  type: roleTypeEnum("type").notNull(),
  isDefault: boolean("is_default").default(false),
  scopeToAssigned: boolean("scope_to_assigned").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});


// Permissions table for RBAC  
export const permissions = pgTable("permissions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  key: varchar("key").notNull().unique(), // e.g., "claims.read", "claims.write", "users.admin"
  name: varchar("name").notNull(),
  description: text("description"),
  resource: varchar("resource").notNull(), // e.g., "claims", "users", "reports"
  action: varchar("action").notNull(), // e.g., "read", "write", "delete", "admin"
  createdAt: timestamp("created_at").defaultNow(),
});


// Role permissions junction table
export const rolePermissions = pgTable("role_permissions", {
  roleId: uuid("role_id").references(() => roles.id, { onDelete: "cascade" }).notNull(),
  permissionId: uuid("permission_id").references(() => permissions.id, { onDelete: "cascade" }).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  pk: primaryKey({ columns: [table.roleId, table.permissionId] }),
}));


// User roles junction table - many-to-many between users and roles, SCOPED PER FIRM.
// orgId makes a role grant belong to one firm (org): a user who is Owner in firm B and a
// plain member in firm A has one row per firm, so a role granted in one firm never applies in
// another. The PK is (user_id, role_id, org_id) so the same role can be held in two firms
// (e.g. Admin in two firms) without colliding; the app-level "one role per firm" invariant is
// enforced by RolePermissionRepository.replaceUserRole (delete all of a user's rows in the firm,
// then insert one). The DB column exists only after drizzle/manual/0100_per-firm-user-roles.sql
// (a FLAGGED MANUAL migration) is applied by hand — see the runbook; the per-firm-roles code
// must be deployed only after 0100 runs.
export const userRoles = pgTable("user_roles", {
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  roleId: uuid("role_id").references(() => roles.id, { onDelete: "cascade" }).notNull(),
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  pk: primaryKey({ columns: [table.userId, table.roleId, table.orgId] }),
  roleIdx: index("user_roles_role_id_idx").on(table.roleId),
  orgIdx: index("user_roles_org_id_idx").on(table.orgId),
}));


// User permission overrides - allows per-user permission customization
export const userPermissionOverrides = pgTable("user_permission_overrides", {
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  permissionId: uuid("permission_id").references(() => permissions.id, { onDelete: "cascade" }).notNull(),
  granted: boolean("granted").notNull(), // true = grant permission, false = revoke permission
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => ({
  pk: primaryKey({ columns: [table.userId, table.permissionId] }),
}));


// User-organization junction table — tracks multi-org membership
// Note: the actual DB table has no `role` column (only user_id, org_id, joined_at)
export const userOrganizations = pgTable("user_organizations", {
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  orgId: uuid("org_id").notNull().references(() => organizations.id, { onDelete: "cascade" }),
  joinedAt: timestamp("joined_at").defaultNow().notNull(),
  isOrgAdmin: boolean("is_org_admin").notNull().default(false),
  canManageBilling: boolean("can_manage_billing").notNull().default(false),
  canAccessAccounting: boolean("can_access_accounting").notNull().default(false),
  hasClaimsAccess: boolean("has_claims_access").notNull().default(true),
}, (table) => ({
  pk: primaryKey({ columns: [table.userId, table.orgId] }),
  orgIdx: index("user_organizations_org_id_idx").on(table.orgId),
}));

// UserWithOrgs extends the base User with multi-org fields returned by /api/auth/user
export type UserWithOrgs = typeof users.$inferSelect & {
  orgs: Array<{ id: string; name: string; slug: string; isActive: boolean }>;
  activeOrgId: string | null;
  roleType?: string;
  roleName?: string;
  membership?: {
    orgId: string | null;
    isOrgAdmin: boolean;
    canManageBilling: boolean;
    canAccessAccounting: boolean;
    hasClaimsAccess: boolean;
  };
  firmBilling?: {
    billingStatus: string;
    cardSaved: boolean;
    cardRequired: boolean;
    /** 'card_required' / 'billing_unavailable': the firm has no saved card and the server blocks everything but setup. */
    cardGate: 'open' | 'card_required' | 'billing_unavailable';
    createdViaBetaSignup: boolean;
    locked: boolean;
    inGracePeriod: boolean;
    trialEndsAt: string | null;
    trialStartAt: string | null;
    firstChargeAt: string | null;
    paidUserCount: number;
    monthlyTotalCents: number;
    showBetaCardBanner: boolean;
    showPaymentFailedNotice: boolean;
  };
  needsFirmOnboarding?: boolean;
};
